import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { Auth } from "aws-amplify";
import {
  menuDeleteIcon,
  impersonateImage,
  viewIcon,
  addFolderIcon,
} from "../../assets/icons";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import {
  EMPTY_STRING,
  POPOVER_BUTTONS,
  ROUTES,
  DEFAULT_LIMIT,
  EMPTY_ARRAY,
  INDEX,
  IMPERSONATED_USER_SUB,
  CACHE_KEYS,
} from "../../constants";
import {
  Card,
  EmptyLayout,
  Pagination,
  SearchBar,
} from "../../components/molecules";
import { actions as appActions } from "../Applications/applications.reducers";
import { actions as organizationActions } from "./organizations.reducers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import {
  selectOrganizationUsers,
  selectOrganizationUsersFilters,
  selectSelectedOrganization,
} from "./organizations.selectors";

const { FETCH_ORGANIZATIONS_USERS } = SPINNER_NAMES;
const { SECOND, FIRST } = INDEX;
const { setSelectedTab } = breadCrumbActions;
const { setShouldShowLeftPane } = appActions;
const {
  setOrganizationUsersFilters,
  setOrganizationUsersId,
  setOrganizationUsers,
} = organizationActions;
const { DELETE, VIEW, IMPERSONATE } = POPOVER_BUTTONS;
const { HOME } = ROUTES;
const { ADMIN_ORGANIZATION, ORGANIZATION } = CACHE_KEYS;

function OrganizationUsers() {
  const { organizationId } = useParams();
  const dispatch = useDispatch();
  const scrollRef = useRef();

  const selectedOrganizationUsers = useSelector(selectOrganizationUsers);
  const organizationUsersFilters = useSelector(selectOrganizationUsersFilters);
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const [pageCount, setPageCount] = useState(SECOND);
  const [orgUsers, setOrgUsers] = useState(EMPTY_ARRAY);
  const [organizationUsersCount, setOrganizationUsersCount] =
    useState(EMPTY_ARRAY);
  const [currentUserSub, setCurrentUserSub] = useState(EMPTY_STRING);

  useEffect(() => {
    if (!isEmpty(selectedOrganizationUsers)) {
      setOrgUsers(
        selectedOrganizationUsers.data[FIRST]?.userOrganizationAssociations
      );
      const organizationUsersCount = selectedOrganizationUsers.count;
      setPageCount(Math.ceil(organizationUsersCount / DEFAULT_LIMIT));
      setOrganizationUsersCount(organizationUsersCount);
    }
  }, [selectedOrganizationUsers]);

  useEffect(() => {
    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [organizationUsersFilters, orgUsers]);

  useEffect(() => {
    if (organizationId) {
      dispatch(setOrganizationUsersId(organizationId));
    }
  }, [dispatch, organizationId]);

  useEffect(() => {
    !isEmpty(selectedOrganizationUsers) &&
      dispatch(setSelectedTab(selectedOrganizationUsers.data[FIRST]));
  }, [dispatch, selectedOrganizationUsers]);

  useEffect(() => {
    dispatch(setShouldShowLeftPane(false));
    return () => {
      dispatch(setOrganizationUsersFilters());
      dispatch(setShouldShowLeftPane(true));
      dispatch(setOrganizationUsers());
      dispatch(setOrganizationUsersId());
    };
  }, [dispatch]);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => setCurrentUserSub(user.attributes.sub))
      .catch((err) => console.warn(err));
  }, []);

  const popOverOptions = (user) => {
    const viewOption = {
      name: VIEW,
      icon: viewIcon,
      handleOnClick: () => console.log(`clicked view ${user}`),
    };
    const deleteOption = {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => console.log(`clicked delete ${user}`),
    };

    const isCurrentUser = user.sub === currentUserSub;
    const isUserAlreadyImpersonating =
      sessionStorage.getItem(IMPERSONATED_USER_SUB) === user.sub;

    const option = {
      name: IMPERSONATE,
      icon: impersonateImage,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        const { sub } = user;
        sessionStorage.setItem(IMPERSONATED_USER_SUB, sub);
        const adminOrg = sessionStorage.getItem(ORGANIZATION);
        sessionStorage.setItem(ADMIN_ORGANIZATION, adminOrg);
        const organization = {
          id: selectedOrganization.id,
          name: selectedOrganization.name
        }
        sessionStorage.setItem(ORGANIZATION, JSON.stringify(organization));
        window.location.assign(HOME);
      },
    };

    const impersonateOption =
      !isCurrentUser && !isUserAlreadyImpersonating ? option : null;
    return [viewOption, deleteOption, impersonateOption].filter(Boolean);
  };

  const handleOnSearch = useCallback(
    (value) =>
      dispatch(
        setOrganizationUsersFilters({
          ...organizationUsersFilters,
          name: value,
          page: 1,
        })
      ),
    [dispatch, organizationUsersFilters]
  );

  const handlePagination = useCallback(
    (value) => {
      dispatch(
        setOrganizationUsersFilters({
          ...organizationUsersFilters,
          page: value + SECOND,
        })
      );
    },
    [dispatch, organizationUsersFilters]
  );

  return (
    <div
      className="scrollbar content overflow-auto p-6 flex flex-1 flex-col  gap-6 px-6 py-4"
      ref={scrollRef}
    >
      <Helmet>
        <title>Users | TavaJS</title>
      </Helmet>
      <div className="flex-col md:flex-row flex mb-4 gap-4 justify-between">
        {!isEmpty(orgUsers) && (
          <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
        )}
      </div>
      <Spinner name={FETCH_ORGANIZATIONS_USERS}>
        {!isEmpty(orgUsers) ? (
          <div className="grid grid-cols-12 gap-4 w-full">
            {orgUsers.map((associatedUser) => (
              <Card
                key={associatedUser.users.id}
                name={associatedUser.users.name}
                createdAt={associatedUser.users.createdAt}
                popOverOptions={popOverOptions(associatedUser.users)}
              />
            ))}
          </div>
        ) : (
          <EmptyLayout
            title="No User Found"
            image={addFolderIcon}
            shouldShowButton={false}
          />
        )}
      </Spinner>
      <Pagination
        handlePagination={handlePagination}
        pagesCount={pageCount}
        activePage={organizationUsersFilters.page - SECOND}
        count={organizationUsersCount}
      />
    </div>
  );
}

export default OrganizationUsers;
